import { DateTime } from 'luxon';

import type { PortfolioEvent, PortfolioMarketPosition } from 'models/portfolioEvent';
import { PriceChangePeriodSort, type SortCriteria } from 'models/search';
import { roundCentsHalfDown } from 'utils/caseing';

export interface SortPortfolioEventsOptions {
    /** Active sort criterion from `usePortfolioUiStore`'s `portfolioOpenSortBy` (or equivalent). */
    criteria: SortCriteria;
    /** When true, reverses the produced ordering. */
    reversed?: boolean;
    /** `usePortfolioUiStore`'s `portfolioOpenPriceChangePeriod` — used by `portfolioPriceChange`. */
    priceChangePeriod?: PriceChangePeriodSort;
    /**
     * `usePortfolioUiStore`'s `portfolioOpenPositionValueChangePeriod` — used by
     * `portfolioPositionValueChange`.
     */
    positionValueChangePeriod?: PriceChangePeriodSort;
    /**
     * `portfolioStore.totalValueInDollars` snapshot — used by
     * `portfolioAllocation` to produce a ratio. Zero is handled gracefully.
     */
    totalValueInDollars?: number;
}

// ---- Per-event aggregate extractors ---------------------------------------
//
// Each extractor turns a PortfolioMarketPosition[] into a single comparable
// number so sort reduces to a plain descending subtract. They're also used
// directly by row components to compute per-event totals (e.g. the `Total`
// row in `PositionPortfolioTable`), keeping one source of truth for
// portfolio aggregates. All extractors operate on the bucket-filtered
// `markets` array each PortfolioEvent carries, with live BinaryMarket /
// Position refs — no indirection through PortfolioStore is needed.

const maxOfMarkets = (markets: PortfolioMarketPosition[], project: (m: PortfolioMarketPosition) => number): number => {
    if (markets.length === 0) return 0;
    let max = -Infinity;
    for (const m of markets) {
        const v = project(m);
        if (v > max) max = v;
    }
    return max;
};

const sumOfMarkets = (markets: PortfolioMarketPosition[], project: (m: PortfolioMarketPosition) => number): number =>
    markets.reduce((acc, m) => acc + project(m), 0);

export const portfolioEventAggregates = {
    /** Σ|position| across the bucket markets. */
    positionCount: (markets: PortfolioMarketPosition[]): number =>
        sumOfMarkets(markets, ({ position }) => Math.abs(position.position)),
    /** Σ marketValueInDollars. */
    marketValue: (markets: PortfolioMarketPosition[]): number =>
        sumOfMarkets(markets, ({ position }) => position.marketValueInDollars),
    /** Σ exposureInDollars (position cost without fees). */
    totalExposure: (markets: PortfolioMarketPosition[]): number =>
        sumOfMarkets(markets, ({ position }) => position.exposureInDollars),
    /**
     * Σ exposureInDollarsWithFees (position cost including approximate fees).
     * Rounds each market the same way its per-row Exposure cell does
     * (`roundCentsHalfDown`) before summing, so the Total ties out to the sum of
     * the displayed rows rather than re-rounding an exact aggregate.
     */
    totalExposureWithFees: (markets: PortfolioMarketPosition[]): number =>
        sumOfMarkets(markets, ({ position }) => roundCentsHalfDown(position.exposureInDollarsWithFees)),
    /** Σ buyingCostInDollars (pre-fees cost of contracts bought). */
    totalBuyingCost: (markets: PortfolioMarketPosition[]): number =>
        sumOfMarkets(markets, ({ position }) => position.buyingCostInDollars),
    /** Σ buyingCostInDollarsWithFees. */
    totalBuyingCostWithFees: (markets: PortfolioMarketPosition[]): number =>
        sumOfMarkets(markets, ({ position }) => position.buyingCostInDollarsWithFees),
    /** Σ totalReturnsInDollars (realized + unrealized). */
    totalReturn: (markets: PortfolioMarketPosition[]): number =>
        sumOfMarkets(markets, ({ position }) => position.totalReturnsInDollars),
    /** totalReturn / totalBuyingCost (0 when no buying cost). */
    totalReturnPercent: (markets: PortfolioMarketPosition[]): number => {
        const cost = portfolioEventAggregates.totalBuyingCost(markets);
        return cost === 0 ? 0 : portfolioEventAggregates.totalReturn(markets) / cost;
    },
    /** Σ unrealizedReturnsInDollars. */
    unrealizedReturn: (markets: PortfolioMarketPosition[]): number =>
        sumOfMarkets(markets, ({ position }) => position.unrealizedReturnsInDollars),
    /** unrealizedReturn / exposureInDollars (0 when no exposure). */
    unrealizedReturnPercent: (markets: PortfolioMarketPosition[]): number => {
        const exposure = portfolioEventAggregates.totalExposure(markets);
        return exposure === 0 ? 0 : portfolioEventAggregates.unrealizedReturn(markets) / exposure;
    },
    /** totalReturn − unrealizedReturn. */
    realizedReturn: (markets: PortfolioMarketPosition[]): number =>
        portfolioEventAggregates.totalReturn(markets) - portfolioEventAggregates.unrealizedReturn(markets),
    /** Σ feesPaid (in cents, matching the raw PositionVo.fees_paid field). */
    feesPaid: (markets: PortfolioMarketPosition[]): number =>
        sumOfMarkets(markets, ({ position }) => position.feesPaid),
    /** Σ totalFeesPaidInDollars (feesPaid / 100). */
    totalFeesInDollars: (markets: PortfolioMarketPosition[]): number =>
        sumOfMarkets(markets, ({ position }) => position.totalFeesPaidInDollars),
    /** Σ market.volume. */
    totalVolume: (markets: PortfolioMarketPosition[]): number => sumOfMarkets(markets, ({ market }) => market.volume),
    /** Σ market.openInterest. */
    totalOpenInterest: (markets: PortfolioMarketPosition[]): number =>
        sumOfMarkets(markets, ({ market }) => market.openInterest),
    /** True when at least one market allows fractional trading. */
    hasFractionalMarket: (markets: PortfolioMarketPosition[]): boolean =>
        markets.some(({ market }) => market.fractionalTradingEnabled),
} as const;

const previousHourPriceChange = (markets: PortfolioMarketPosition[]): number =>
    sumOfMarkets(markets, ({ position }) => position.previousHourPriceChangeInDollars);

const previousDayPriceChange = (markets: PortfolioMarketPosition[]): number =>
    sumOfMarkets(markets, ({ position }) => position.previousDayPriceChangeInDollars);

const previousWeekPriceChange = (markets: PortfolioMarketPosition[]): number =>
    sumOfMarkets(markets, ({ position }) => position.previousWeekPriceChangeInDollars);

const lastTradedPriceKey = (markets: PortfolioMarketPosition[]): number =>
    maxOfMarkets(markets, ({ market, position }) =>
        position.position > 0 ? market.lastPrice : 100 - market.lastPrice,
    );

const averagePriceKey = (markets: PortfolioMarketPosition[]): number =>
    maxOfMarkets(markets, ({ position }) => position.averagePriceInDollars);

const buySellKey = (markets: PortfolioMarketPosition[]): number =>
    maxOfMarkets(markets, ({ market, position }) => (position.position > 0 ? market.yesAsk : market.noAsk));

/**
 * Earliest close date across the bucket markets, in milliseconds. Drives the
 * `portfolioClosing` / `earliestCloseDate` sort (lowest first = closing
 * soonest), so multi-market events like KXNFLGAME line up with the per-market
 * DTE values rendered in their sub-rows. Returns `Infinity` for events with
 * no parsed close — the calling sort case picks a direction-aware sentinel
 * via `closeKey` so unknown-close rows land at the visual bottom in both
 * the ascending and the reversed (descending) orderings.
 */
const earliestMarketCloseMs = (markets: PortfolioMarketPosition[]): number => {
    let min = Infinity;
    for (const { market } of markets) {
        const ms = DateTime.fromISO(market.closeDate).valueOf();
        if (Number.isFinite(ms) && ms < min) min = ms;
    }
    return min;
};

/**
 * Latest close date across the bucket markets, in milliseconds. Used by the
 * `latestCloseDate` sort so events with the furthest-out market come first.
 */
const latestMarketCloseMs = (markets: PortfolioMarketPosition[]): number => {
    let max = -Infinity;
    for (const { market } of markets) {
        const ms = DateTime.fromISO(market.closeDate).valueOf();
        if (Number.isFinite(ms) && ms > max) max = ms;
    }
    return max;
};

/**
 * Resolve a comparable close-date key for an event: the per-market aggregate
 * (`earliestMarketCloseMs` / `latestMarketCloseMs`) when at least one market
 * parses, otherwise the event-level `closeDate`, otherwise `noDataSentinel`.
 * Callers pick a sentinel that pushes "no close date" rows to the visual end
 * of the active sort (`Infinity` for ascending, `-Infinity` for descending).
 */
const closeKey = (
    e: PortfolioEvent,
    fromMarkets: (markets: PortfolioMarketPosition[]) => number,
    noDataSentinel: number,
): number => {
    const fromMarketsMs = fromMarkets(e.markets);
    if (Number.isFinite(fromMarketsMs)) return fromMarketsMs;
    const fromEventMs = DateTime.fromISO(e.closeDate).valueOf();
    return Number.isFinite(fromEventMs) ? fromEventMs : noDataSentinel;
};

const priceChangeKeyFor = (
    period: PriceChangePeriodSort | undefined,
): ((markets: PortfolioMarketPosition[]) => number) | null => {
    switch (period) {
        case PriceChangePeriodSort.HOUR:
            return previousHourPriceChange;
        case PriceChangePeriodSort.DAY:
            return previousDayPriceChange;
        case PriceChangePeriodSort.WEEK:
            return previousWeekPriceChange;
        default:
            return null;
    }
};

const positionValueChangeKeyFor = (
    period: PriceChangePeriodSort | undefined,
): ((markets: PortfolioMarketPosition[]) => number) | null => {
    const priceKey = priceChangeKeyFor(period);
    if (!priceKey) return null;
    return (markets) => priceKey(markets) * portfolioEventAggregates.positionCount(markets);
};

const byDesc = (key: (markets: PortfolioMarketPosition[]) => number) => (a: PortfolioEvent, b: PortfolioEvent) =>
    key(b.markets) - key(a.markets);

/**
 * Sort the provided `PortfolioEvent[]` in place using the portfolio sort
 * criteria. Returns the same array for chaining. Portfolio-specific sort
 * logic used to live in `MarketStore.baseSortEvent` and indirectly accessed
 * `PortfolioStore.eventPositions`. It now operates directly on the
 * `PortfolioMarketPosition[]` refs carried by each `PortfolioEvent`.
 *
 * Criteria that aren't portfolio-related (e.g. `timeline`, `largestRecentVolume`)
 * fall through unsorted — those paths belong to the market-store-level sorter
 * and should never reach this helper.
 */
export const sortPortfolioEvents = (
    events: PortfolioEvent[],
    {
        criteria,
        reversed,
        priceChangePeriod,
        positionValueChangePeriod,
        totalValueInDollars,
    }: SortPortfolioEventsOptions,
): PortfolioEvent[] => {
    switch (criteria) {
        case 'portfolioEvents':
            events.sort((a, b) => a.title.localeCompare(b.title));
            break;
        case 'portfolioContracts':
            events.sort(byDesc(portfolioEventAggregates.positionCount));
            break;
        case 'portfolioLtp':
            events.sort(byDesc(lastTradedPriceKey));
            break;
        case 'portfolioUnrealized':
            events.sort(byDesc(portfolioEventAggregates.unrealizedReturn));
            break;
        case 'portfolioRealized':
            events.sort(byDesc(portfolioEventAggregates.realizedReturn));
            break;
        case 'portfolioFees':
            events.sort(byDesc(portfolioEventAggregates.feesPaid));
            break;
        case 'portfolioAllocation': {
            const denom = totalValueInDollars && totalValueInDollars !== 0 ? totalValueInDollars : 1;
            events.sort(byDesc((markets) => portfolioEventAggregates.marketValue(markets) / denom));
            break;
        }
        case 'portfolioAvg':
            events.sort(byDesc(averagePriceKey));
            break;
        case 'portfolioExposure':
            events.sort(byDesc(portfolioEventAggregates.totalExposure));
            break;
        case 'portfolioValue':
        case 'portfolioMarketValue':
            events.sort(byDesc(portfolioEventAggregates.marketValue));
            break;
        case 'portfolioVolume':
            events.sort(byDesc(portfolioEventAggregates.totalVolume));
            break;
        case 'portfolioBuy':
            events.sort(byDesc(buySellKey));
            break;
        case 'portfolioInterest':
            events.sort(byDesc(portfolioEventAggregates.totalOpenInterest));
            break;
        case 'portfolioTotalPercentageReturn':
            events.sort(byDesc(portfolioEventAggregates.totalReturnPercent));
            break;
        case 'portfolioTotalReturn':
            events.sort(byDesc(portfolioEventAggregates.totalReturn));
            break;
        case 'portfolioNewestFirst':
            events.sort((a, b) => DateTime.fromISO(b.openDate).valueOf() - DateTime.fromISO(a.openDate).valueOf());
            break;
        case 'portfolioPriceChange': {
            const key = priceChangeKeyFor(priceChangePeriod);
            if (key) events.sort(byDesc(key));
            break;
        }
        case 'portfolioPositionValueChange': {
            const key = positionValueChangeKeyFor(positionValueChangePeriod);
            if (key) events.sort(byDesc(key));
            break;
        }
        case 'portfolioRecentTrades': {
            const lastUpdatedTs = (e: PortfolioEvent): number => {
                if (!e.lastUpdatedTs) return 0;
                const secs = DateTime.fromISO(e.lastUpdatedTs).toSeconds();
                return Number.isFinite(secs) ? secs : 0;
            };
            events.sort((a, b) => lastUpdatedTs(b) - lastUpdatedTs(a));
            break;
        }
        case 'portfolioClosing':
        case 'earliestCloseDate':
        case 'latestCloseDate': {
            // Sort by per-market close so multi-market events line up with
            // the per-market DTE values rendered in their sub-rows. The
            // `reversed` flag is folded into the comparator (and into the
            // direction-aware no-data sentinel) and we early-return to skip
            // the unconditional `events.reverse()` at the bottom of this
            // function — otherwise the sentinel that pushes unknown-close
            // events to the visual end of the ascending sort would be
            // flipped to the very top after reversal, giving rows with no
            // parsed close date the appearance of being the "latest"
            // closing positions.
            const fromMarkets = criteria === 'latestCloseDate' ? latestMarketCloseMs : earliestMarketCloseMs;
            const defaultsAscending = criteria !== 'latestCloseDate';
            const ascending = defaultsAscending !== !!reversed;
            const sentinel = ascending ? Infinity : -Infinity;
            const sign = ascending ? 1 : -1;
            events.sort((a, b) => sign * (closeKey(a, fromMarkets, sentinel) - closeKey(b, fromMarkets, sentinel)));
            return events;
        }
        default:
            // Unhandled criterion (likely not portfolio-related) — leave the
            // list alone and let the caller fall back to its own sort.
            break;
    }
    if (reversed) events.reverse();
    return events;
};
